import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Modal,
  message,
  Spin,
  Badge,
  Radio,
  Statistic,
  Table,
  Tag,
  QRCode,
  Space,
  Typography,
  Divider,
  Alert,
  Progress,
  Empty,
  Tooltip,
} from 'antd';
import {
  PayCircleOutlined,
  GiftOutlined,
  FireOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { useTheme } from '../../context/ThemeContext';
import type { ColumnsType } from 'antd/es/table';
import api from '../../services/api';

const { Title, Text, Paragraph } = Typography;

// 类型定义
interface RechargePackage {
  id: number;
  name: string;
  amount: number;
  bonus_amount: number;
  total_amount: number;
  is_hot: boolean;
  is_enabled: boolean;
  sort: number;
  description: string;
  total_sales: number;
  total_sales_amount: number;
}

interface PaymentMethod {
  id: number;
  name: string;
  code: string;
  description: string;
  icon: string;
  is_enabled: boolean;
  sort: number;
  min_amount: number;
  max_amount: number;
  fee_rate: number;
}

interface RechargeOrder {
  id: number;
  order_no: string;
  package_id: number;
  payment_method_id: number;
  amount: number;
  bonus_amount: number;
  total_amount: number;
  status: string;
  payment_qr_code: string;
  payment_order_no: string;
  client_ip: string;
  failure_reason: string;
  paid_at: string;
  expired_at: string;
  created_at: string;
  package?: RechargePackage;
  payment_method?: PaymentMethod;
}

interface PaymentStatusResult {
  order_no: string;
  payment_order_no: string;
  status: string;
  paid_amount: number;
  paid_at: string;
  failure_reason: string;
}

const RechargeManagement: React.FC = () => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState<RechargePackage[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [orders, setOrders] = useState<RechargeOrder[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<RechargePackage | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  const [currentOrder, setCurrentOrder] = useState<RechargeOrder | null>(null);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [polling, setPolling] = useState(false);

  // 获取充值套餐
  const fetchPackages = useCallback(async () => {
    try {
      const response = await api.get('/api/v1/recharge/packages');
      if (response.data.code === 200) {
        setPackages(response.data.data || []);
      } else {
        message.error(response.data.message || '获取充值套餐失败');
      }
    } catch (error) {
      console.error('获取充值套餐失败:', error);
      message.error('获取充值套餐失败');
    }
  }, []);

  // 获取支付方式
  const fetchPaymentMethods = useCallback(async () => {
    try {
      const response = await api.get('/api/v1/recharge/payment-methods');
      if (response.data.code === 200) {
        const methods = response.data.data || [];
        setPaymentMethods(methods);
        if (methods.length > 0) {
          setSelectedPaymentMethod(methods[0].code);
        }
      } else {
        message.error(response.data.message || '获取支付方式失败');
      }
    } catch (error) {
      console.error('获取支付方式失败:', error);
      message.error('获取支付方式失败');
    }
  }, []);

  // 获取充值记录
  const fetchOrders = useCallback(async () => {
    try {
      const response = await api.get('/api/v1/recharge/orders', {
        params: { page: 1, page_size: 10 }
      });
      if (response.data.code === 200) {
        setOrders(response.data.data?.orders || []);
      }
    } catch (error) {
      console.error('获取充值记录失败:', error);
    }
  }, []);

  // 创建充值订单
  const createOrder = async () => {
    if (!selectedPackage || !selectedPaymentMethod) {
      message.error('请选择充值套餐和支付方式');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/v1/recharge/orders', {
        package_id: selectedPackage.id,
        payment_method_code: selectedPaymentMethod,
      });

      if (response.data.code === 200) {
        const order = response.data.data;
        setCurrentOrder(order);
        setPaymentModalVisible(true);
        setCountdown(30 * 60); // 30分钟倒计时
        startPolling(order.order_no);
        message.success('订单创建成功，请完成支付');
      } else {
        message.error(response.data.message || '创建订单失败');
      }
    } catch (error: any) {
      console.error('创建订单失败:', error);
      message.error(error.response?.data?.message || '创建订单失败');
    } finally {
      setLoading(false);
    }
  };

  // 查询支付状态
  const queryPaymentStatus = async (orderNo: string) => {
    try {
      const response = await api.get(`/api/v1/recharge/payment-status/${orderNo}`);
      if (response.data.code === 200) {
        const result: PaymentStatusResult = response.data.data;
        
        if (result.status === 'paid') {
          setPolling(false);
          message.success('支付成功！余额已到账');
          setPaymentModalVisible(false);
          fetchOrders(); // 刷新订单列表
          return true;
        } else if (result.status === 'failed' || result.status === 'expired') {
          setPolling(false);
          message.error(result.failure_reason || '支付失败');
          setPaymentModalVisible(false);
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('查询支付状态失败:', error);
      return false;
    }
  };

  // 开始轮询支付状态
  const startPolling = (orderNo: string) => {
    setPolling(true);
    const interval = setInterval(async () => {
      const finished = await queryPaymentStatus(orderNo);
      if (finished) {
        clearInterval(interval);
      }
    }, 3000); // 每3秒查询一次

    // 30分钟后停止轮询
    setTimeout(() => {
      clearInterval(interval);
      setPolling(false);
    }, 30 * 60 * 1000);
  };

  // 倒计时效果
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0 && paymentModalVisible) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (countdown === 0 && paymentModalVisible) {
      setPolling(false);
      message.warning('订单已过期');
      setPaymentModalVisible(false);
    }
    return () => clearTimeout(timer);
  }, [countdown, paymentModalVisible]);

  // 格式化倒计时
  const formatCountdown = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // 获取状态标签
  const getStatusTag = (status: string) => {
    const statusMap = {
      pending: { color: 'processing', text: '待支付' },
      paid: { color: 'success', text: '已支付' },
      failed: { color: 'error', text: '支付失败' },
      expired: { color: 'default', text: '已过期' },
      cancelled: { color: 'default', text: '已取消' },
    };
    const statusInfo = statusMap[status as keyof typeof statusMap] || { color: 'default', text: status };
    return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
  };

  // 订单表格列定义
  const orderColumns: ColumnsType<RechargeOrder> = [
    {
      title: '订单号',
      dataIndex: 'order_no',
      key: 'order_no',
      width: 160,
      render: (text) => <Text copyable={{ text }}>{text}</Text>,
    },
    {
      title: '套餐',
      dataIndex: 'package',
      key: 'package',
      render: (pkg: RechargePackage) => pkg?.name || '-',
    },
    {
      title: '充值金额',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => `¥${amount.toFixed(2)}`,
    },
    {
      title: '赠送金额',
      dataIndex: 'bonus_amount',
      key: 'bonus_amount',
      render: (bonus) => bonus > 0 ? `¥${bonus.toFixed(2)}` : '-',
    },
    {
      title: '到账金额',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (total) => <Text strong>¥{total.toFixed(2)}</Text>,
    },
    {
      title: '支付方式',
      dataIndex: 'payment_method',
      key: 'payment_method',
      render: (method: PaymentMethod) => method?.name || '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (time) => new Date(time).toLocaleString(),
    },
  ];

  useEffect(() => {
    fetchPackages();
    fetchPaymentMethods();
    fetchOrders();
  }, [fetchPackages, fetchPaymentMethods, fetchOrders]);

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <PayCircleOutlined /> 账户充值
      </Title>
      <Paragraph type="secondary">
        选择合适的充值套餐，享受更多优惠和赠送额度
      </Paragraph>

      {/* 充值套餐选择 */}
      <Card title="选择充值套餐" style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]}>
          {packages.map((pkg) => (
            <Col xs={24} sm={12} md={8} lg={6} key={pkg.id}>
              <Card
                hoverable
                className={selectedPackage?.id === pkg.id ? 'selected-package' : ''}
                style={{
                  border: selectedPackage?.id === pkg.id ? '2px solid #1890ff' : '1px solid #d9d9d9',
                  position: 'relative',
                }}
                onClick={() => setSelectedPackage(pkg)}
              >
                {pkg.is_hot && (
                  <Badge.Ribbon text="热门" color="red">
                    <div />
                  </Badge.Ribbon>
                )}
                
                <div style={{ textAlign: 'center' }}>
                  <Title level={4} style={{ margin: '8px 0' }}>
                    {pkg.name}
                  </Title>
                  
                  <div style={{ margin: '16px 0' }}>
                    <Statistic
                      title="充值金额"
                      value={pkg.amount}
                      precision={2}
                      prefix="¥"
                      valueStyle={{ color: '#1890ff', fontSize: '24px' }}
                    />
                  </div>

                  {pkg.bonus_amount > 0 && (
                    <div style={{ margin: '8px 0' }}>
                      <Text type="success">
                        <GiftOutlined /> 赠送 ¥{pkg.bonus_amount.toFixed(2)}
                      </Text>
                    </div>
                  )}

                  <div style={{ margin: '8px 0' }}>
                    <Text strong>
                      到账金额：¥{pkg.total_amount.toFixed(2)}
                    </Text>
                  </div>

                  <Paragraph type="secondary" style={{ fontSize: '12px', margin: '8px 0' }}>
                    {pkg.description}
                  </Paragraph>

                  {pkg.total_sales > 0 && (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      已售 {pkg.total_sales} 份
                    </Text>
                  )}
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </Card>

      {/* 支付方式选择 */}
      <Card title="选择支付方式" style={{ marginBottom: '24px' }}>
        <Radio.Group
          value={selectedPaymentMethod}
          onChange={(e) => setSelectedPaymentMethod(e.target.value)}
          style={{ width: '100%' }}
        >
          <Row gutter={[16, 16]}>
            {paymentMethods.map((method) => (
              <Col xs={24} sm={12} md={8} lg={6} key={method.id}>
                <Radio.Button
                  value={method.code}
                  style={{
                    width: '100%',
                    height: '80px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', marginBottom: '4px' }}>
                      {method.code === 'alipay' ? '💙' : '💚'}
                    </div>
                    <div>{method.name}</div>
                    <div style={{ fontSize: '12px', color: '#999' }}>
                      {method.description}
                    </div>
                  </div>
                </Radio.Button>
              </Col>
            ))}
          </Row>
        </Radio.Group>
      </Card>

      {/* 充值按钮和操作 */}
      <Card>
        <Row justify="space-between" align="middle">
          <Col>
            {selectedPackage && (
              <Space size="large">
                <Statistic
                  title="充值金额"
                  value={selectedPackage.amount}
                  precision={2}
                  prefix="¥"
                />
                {selectedPackage.bonus_amount > 0 && (
                  <Statistic
                    title="赠送金额"
                    value={selectedPackage.bonus_amount}
                    precision={2}
                    prefix="¥"
                    valueStyle={{ color: '#52c41a' }}
                  />
                )}
                <Statistic
                  title="到账金额"
                  value={selectedPackage.total_amount}
                  precision={2}
                  prefix="¥"
                  valueStyle={{ color: '#1890ff', fontWeight: 'bold' }}
                />
              </Space>
            )}
          </Col>
          <Col>
            <Space>
              <Button
                icon={<HistoryOutlined />}
                onClick={() => setHistoryModalVisible(true)}
              >
                充值记录
              </Button>
              <Button
                type="primary"
                size="large"
                icon={<PayCircleOutlined />}
                loading={loading}
                disabled={!selectedPackage || !selectedPaymentMethod}
                onClick={createOrder}
              >
                立即充值
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 支付弹窗 */}
      <Modal
        title="完成支付"
        open={paymentModalVisible}
        onCancel={() => {
          setPaymentModalVisible(false);
          setPolling(false);
        }}
        footer={[
          <Button key="cancel" onClick={() => setPaymentModalVisible(false)}>
            取消支付
          </Button>,
          <Button
            key="refresh"
            icon={<ReloadOutlined />}
            loading={polling}
            onClick={() => currentOrder && queryPaymentStatus(currentOrder.order_no)}
          >
            刷新状态
          </Button>,
        ]}
        width={480}
        centered
      >
        {currentOrder && (
          <div style={{ textAlign: 'center' }}>
            <Alert
              message={`订单将在 ${formatCountdown(countdown)} 后过期`}
              type="warning"
              style={{ marginBottom: '16px' }}
            />

            <div style={{ margin: '16px 0' }}>
              <Text>订单号：</Text>
              <Text copyable={{ text: currentOrder.order_no }}>
                {currentOrder.order_no}
              </Text>
            </div>

            <div style={{ margin: '16px 0' }}>
              <Text>支付金额：</Text>
              <Text strong style={{ fontSize: '18px', color: '#1890ff' }}>
                ¥{currentOrder.amount.toFixed(2)}
              </Text>
            </div>

            {currentOrder.payment_qr_code && (
              <div style={{ margin: '24px 0' }}>
                <QRCode
                  value={currentOrder.payment_qr_code}
                  size={200}
                  style={{ margin: '0 auto' }}
                />
                <Paragraph style={{ marginTop: '16px', color: '#666' }}>
                  请使用{paymentMethods.find(m => m.id === currentOrder.payment_method_id)?.name}扫描二维码完成支付
                </Paragraph>
              </div>
            )}

            {polling && (
              <div style={{ margin: '16px 0' }}>
                <Spin size="small" />
                <Text style={{ marginLeft: '8px' }}>正在等待支付...</Text>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 充值记录弹窗 */}
      <Modal
        title="充值记录"
        open={historyModalVisible}
        onCancel={() => setHistoryModalVisible(false)}
        footer={null}
        width={1000}
      >
        <Table
          columns={orderColumns}
          dataSource={orders}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            showQuickJumper: true,
          }}
          scroll={{ x: 800 }}
        />
      </Modal>

      <style>{`
        .selected-package {
          box-shadow: 0 4px 12px rgba(24, 144, 255, 0.15);
        }
      `}</style>
    </div>
  );
};

export default RechargeManagement; 